import { useLayoutEffect, useRef, useState } from "react";
import { Button, Cluster, Stack } from "@salesapp/ui";
import type { Playbook, PlaybookStep } from "./playbooks";
import "./guide.css";

interface Hole {
  top: number;
  left: number;
  width: number;
  height: number;
}

export function Stars({ completed, total }: { completed: number; total: number }) {
  return (
    <p className="guide-stars" aria-label={`${completed} of ${total} guided tours completed`}>
      <span aria-hidden="true">
        {Array.from({ length: total }, (_, i) => (i < completed ? "★" : "☆")).join("")}
      </span>
      <span className="guide-kicker"> {completed} of {total} guided tours completed</span>
    </p>
  );
}

export function Spotlight({
  hole,
  playbook,
  step,
  index,
  total,
  onNext,
  onExit,
  nextEnabled = true,
  intro = false,
  onIntroDone,
  picker,
  onPick,
  locked = false,
  onSkip,
  docked = false,
  completedIds = [],
  done,
  onDoneStart,
  onDoneClose,
}: {
  hole: Hole | null;
  playbook: Playbook | null;
  step: PlaybookStep | null;
  index: number;
  total: number;
  onNext: () => void;
  onExit: () => void;
  nextEnabled?: boolean;
  intro?: boolean;
  onIntroDone?: () => void;
  picker?: Playbook[] | null;
  onPick?: (id: Playbook["id"]) => void;
  /** Required setup: no Exit button, no Esc. The only way forward is through the steps. */
  locked?: boolean;
  /** Lockout safety valve — offered only when a locked step's target has been missing for a long time. */
  onSkip?: () => void;
  /** Float the card top right (fixed, so it stays put while the page scrolls). */
  docked?: boolean;
  completedIds?: Playbook["id"][];
  /** Shown after a tour is finished: stars for progress, plus an optional next tour to offer. */
  done?: { title: string; completed: number; total: number; offer: Playbook | null } | null;
  onDoneStart?: (id: Playbook["id"]) => void;
  onDoneClose?: () => void;
}) {
  const last = index >= total - 1;
  const pad = 8;
  const vw = typeof window === "undefined" ? 0 : window.innerWidth;
  const vh = typeof window === "undefined" ? 0 : window.innerHeight;
  const showHole = Boolean(hole) && !intro && !picker && !done;
  const dims = showHole && hole
    ? [
        { top: 0, left: 0, width: vw, height: Math.max(0, hole.top - pad) },
        { top: hole.top + hole.height + pad, left: 0, width: vw, height: Math.max(0, vh - (hole.top + hole.height + pad)) },
        { top: Math.max(0, hole.top - pad), left: 0, width: Math.max(0, hole.left - pad), height: hole.height + pad * 2 },
        {
          top: Math.max(0, hole.top - pad),
          left: hole.left + hole.width + pad,
          width: Math.max(0, vw - (hole.left + hole.width + pad)),
          height: hole.height + pad * 2,
        },
      ]
    : [{ top: 0, left: 0, width: vw, height: vh }];

  const welcome = intro && locked && playbook?.id === "org-admin";
  const dock = showHole && docked;
  // The docked card sits top right. When the highlighted control is under that spot (a toolbar
  // button on the right, say), the card would cover the very thing the user must click, so it
  // drops to the bottom right instead. Measured before paint, so it never flashes over the target.
  const cardRef = useRef<HTMLDivElement>(null);
  const [dockLow, setDockLow] = useState(false);
  useLayoutEffect(() => {
    const node = cardRef.current;
    if (!dock || !hole || !node) {
      setDockLow(false);
      return;
    }
    const { width, height } = node.getBoundingClientRect();
    const gap = Number.parseFloat(getComputedStyle(node).right) || 0;
    const left = vw - gap - width;
    const covers = (top: number) => hole.left + hole.width + pad > left && hole.left - pad < left + width
      && hole.top + hole.height + pad > top && hole.top - pad < top + height;
    setDockLow(covers(gap) && !covers(vh - gap - height));
  }, [dock, hole, vw, vh, step?.id]);
  const cardLeft = showHole && hole ? Math.min(Math.max(hole.left, 16), Math.max(16, vw - 360)) : undefined;
  const cardTop = showHole && hole ? Math.min(hole.top + hole.height + 16, Math.max(16, vh - 280)) : undefined;
  const exitHint = locked ? "" : " Esc exits.";
  const footer = step?.advanceOnClick
    ? `Click the highlighted control to continue. Next stays grey until you do.${exitHint}`
    : step?.requireFilled
      ? `Type in the highlighted field. Next unlocks when it has a value.${exitHint}`
      : locked
        ? "This setup is required before you start selling."
        : "Esc also exits. Replay later from your avatar or Help.";

  return (
    <>
      {dims.map((box, i) => (
        <div
          key={i}
          className="guide-dim"
          style={{ top: box.top, left: box.left, width: box.width, height: box.height }}
          onMouseDown={(event) => event.preventDefault()}
          onWheel={(event) => window.scrollBy(0, event.deltaY)}
        />
      ))}
      {showHole && hole && (
        <div
          className="guide-spot"
          style={{ top: hole.top - pad, left: hole.left - pad, width: hole.width + pad * 2, height: hole.height + pad * 2 }}
        />
      )}
      <div
        ref={cardRef}
        className={`guide-card${showHole ? "" : " guide-card--center"}${dock ? " guide-card--dock" : ""}${dock && dockLow ? " guide-card--dock-low" : ""}${welcome ? " guide-card--welcome" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={
          done
            ? "Tour complete"
            : picker
            ? "Guided tours"
            : welcome
              ? "Welcome to Saleslox"
              : intro
                ? "How this walkthrough works"
              : step?.title ?? "Guided tour"
        }
        style={showHole && !dock && cardLeft != null && cardTop != null ? { left: cardLeft, top: cardTop } : undefined}
      >
        {done && onDoneClose ? (
          <Stack gap={4}>
            <Stack gap={2}>
              <Stars completed={done.completed} total={done.total} />
              <h2>{done.title} complete</h2>
              <p>
                {done.offer
                  ? `Well done. Would you like to continue with “${done.offer.title}”? ${done.offer.blurb}`
                  : "Well done. Your progress is saved, and you can open any guided tour from your user icon in the top right."}
              </p>
            </Stack>
            <div className="guide-actions">
              <Button type="button" variant="ghost" onClick={onDoneClose}>{done.offer ? "Not now" : "Close"}</Button>
              {done.offer && onDoneStart ? (
                <Button type="button" onClick={() => onDoneStart(done.offer!.id)}>Start</Button>
              ) : null}
            </div>
          </Stack>
        ) : picker && onPick ? (
          <Stack gap={4}>
            <Stack gap={2}>
              <p className="guide-kicker">Guided tours</p>
              <Stars completed={completedIds.length} total={picker.length} />
              <h2>Choose a walkthrough</h2>
              <p>Each tour highlights where to click. Next stays grey until you click the highlighted control. You can exit at any time and replay later.</p>
            </Stack>
            <div className="guide-picker">
              {picker.map((item) => (
                <button key={item.id} type="button" className="guide-pick" onClick={() => onPick(item.id)}>
                  <strong>{completedIds.includes(item.id) ? "★ " : ""}{item.title}</strong>
                  <span className="guide-kicker">{item.audience} — {item.blurb}</span>
                </button>
              ))}
            </div>
            <div className="guide-actions">
              <Button type="button" variant="ghost" onClick={onExit}>Close</Button>
            </div>
          </Stack>
        ) : welcome && onIntroDone ? (
          <Stack gap={5}>
            <Stack gap={3}>
              <h2>Thank you for signing up with Saleslox</h2>
              <p>
                To help you get the most from your trial, we recommend working through our guided tours. They
                introduce the product step by step, and you can open them at any time by clicking your user icon
                in the top right corner.
              </p>
              <p>
                To begin, we will take you through the required setup for your company, including your currency,
                timezone, and other key details. Select OK when you are ready to start.
              </p>
            </Stack>
            <div className="guide-actions">
              <Button type="button" onClick={onIntroDone}>OK</Button>
            </div>
          </Stack>
        ) : intro && playbook && onIntroDone ? (
          <Stack gap={4}>
            <Stack gap={2}>
              <p className="guide-kicker">{playbook.title}</p>
              <h2>How this walkthrough works</h2>
              <p>
                Click each highlighted control to move on. Next stays grey until you do — it will not click for you.
                When a field is highlighted, type first; Next unlocks once it has a value.
                {locked
                  ? " This setup is required for your trial, so it cannot be closed until you finish it."
                  : " Esc exits anytime."}
              </p>
            </Stack>
            <Cluster gap={2} justify={locked ? "end" : "between"}>
              {locked ? null : <Button type="button" variant="ghost" onClick={onExit}>Exit</Button>}
              <div className="guide-actions">
                <Button type="button" onClick={onIntroDone}>Got it</Button>
              </div>
            </Cluster>
          </Stack>
        ) : playbook && step ? (
          <Stack gap={4}>
            <Stack gap={2}>
              <p className="guide-kicker">
                {playbook.title} · {index + 1} of {total}
              </p>
              <h2>{step.title}</h2>
              <p>{step.body}</p>
            </Stack>
            <Cluster gap={2} justify={locked && !onSkip ? "end" : "between"}>
              {locked ? (
                onSkip ? <Button type="button" variant="ghost" onClick={onSkip}>Skip this step</Button> : null
              ) : (
                <Button type="button" variant="ghost" onClick={onExit}>Exit</Button>
              )}
              <div className="guide-actions">
                <Button type="button" onClick={onNext} disabled={!nextEnabled}>
                  {last ? "Done" : "Next"}
                </Button>
              </div>
            </Cluster>
            <p className="guide-kicker">{footer}</p>
          </Stack>
        ) : null}
      </div>
    </>
  );
}
